"use client";

import { useState } from "react";
import Link from "next/link";
import { z } from "zod/v4";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SocialAuthButtons } from "@/components/auth/social-auth-buttons";
import { signInDemo } from "@/app/(auth)/actions";
import { verifyCredentials } from "@/lib/auth-demo";

const loginSchema = z.object({
  email: z.email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

/** Read ?callbackUrl from the address bar (set by the proxy login wall). */
function getCallbackUrl(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return new URLSearchParams(window.location.search).get("callbackUrl") ?? undefined;
}

export default function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState("");
  const [pending, setPending] = useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const result = loginSchema.safeParse({ email, password });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const key = String(issue.path[0]);
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setFormError("");
    setPending(true);
    // Sign in with the email + password registered on this device.
    const check = await verifyCredentials(email, password);
    if (!check.ok) {
      // One generic message regardless of whether the account exists, so the
      // form can't be used to probe which emails are registered.
      setFormError("That email and password do not match.");
      setPending(false);
      return;
    }
    // Remember the email + name for the profile and certificates, and clear any
    // prior role/company so a shared device doesn't carry the last user's
    // manager status into this account.
    try {
      localStorage.setItem("tutorials:email", email.trim());
      if (check.name) localStorage.setItem("tutorials:name", check.name);
      localStorage.removeItem("tutorials:role");
      localStorage.removeItem("tutorials:company");
    } catch {
      /* ignore */
    }
    await signInDemo(getCallbackUrl());
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <span className="eyebrow">Welcome back</span>
        <h1 className="mt-2 font-heading text-3xl font-semibold text-dark">
          Sign in
        </h1>
        <p className="mt-2 text-sm text-mid">
          Pick up where you left off. Your progress, certificates and rewards
          are all where you left them.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input
          label="Email address"
          type="email"
          placeholder="you@example.com"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
        />

        <Input
          label="Password"
          type="password"
          placeholder="Enter your password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
        />

        <div className="flex justify-end">
          <Link
            href="/forgot-password"
            className="text-sm font-medium text-primary underline-offset-2 hover:text-secondary hover:underline"
          >
            Forgot your password?
          </Link>
        </div>

        {formError && (
          <p role="alert" className="text-sm font-medium text-error">
            {formError}
          </p>
        )}

        <Button type="submit" variant="primary" className="w-full" disabled={pending}>
          {pending ? "Signing in…" : "Sign in"}
        </Button>
      </form>

      <SocialAuthButtons action="Sign in" />

      {/* Sign up link */}
      <p className="text-center text-sm text-mid">
        Don&apos;t have an account?{" "}
        <Link
          href="/signup"
          className="font-semibold text-primary underline-offset-2 hover:text-secondary hover:underline"
        >
          Create one
        </Link>
      </p>
    </div>
  );
}
